// @givmo/app-admin — build/runtime env (Vite `import.meta.env`).
//
// Unlike the SSR donate app (which reads RUNTIME `process.env` per request), the admin app is a
// STATIC SPA: its config is read from Vite's `import.meta.env` at BUILD time (the deploy pipeline
// injects `VITE_*` vars per environment, mirroring the donate Cloud Run env vars). No secrets are
// baked — Firebase web config + the reCAPTCHA Enterprise site key + the Sentry DSN are all public in
// the web-client sense, but still per-environment and therefore injected, never hardcoded.
//
// The API base URL is env-injected (NO hardcoded prod URL as the only option): `VITE_API_BASE_URL`
// wins when set, otherwise a per-env default keyed off `VITE_GIVMO_ENV` (api-dev.givmo.io for
// dev/local, api-staging.givmo.io for staging, api.givmo.io for prod).

/** The deploy environments. Read from `VITE_GIVMO_ENV`, defaulting to `local`. */
export type DeployEnv = 'local' | 'dev' | 'staging' | 'prod';

const DEPLOY_ENVS: readonly DeployEnv[] = ['local', 'dev', 'staging', 'prod'];

/**
 * Per-env API base default. Env-injected `VITE_API_BASE_URL` overrides this. dev/local point at the
 * dev API, staging at staging, and prod at production. The branded hosts are the defaults; an
 * explicit `VITE_API_BASE_URL` (e.g. a Cloud Run URL) takes priority.
 */
const API_BASE_URL_BY_ENV: Record<DeployEnv, string> = {
  local: 'https://api-dev.givmo.io',
  dev: 'https://api-dev.givmo.io',
  staging: 'https://api-staging.givmo.io',
  prod: 'https://api.givmo.io',
};

/** Firebase web config — public (client-shippable) per the Firebase web SDK model. */
export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  measurementId?: string;
}

/** The validated, typed env subset the app consumes. NEVER includes secrets. */
export interface AdminEnv {
  deployEnv: DeployEnv;
  apiBaseUrl: string;
  firebase: FirebaseWebConfig;
  recaptchaEnterpriseSiteKey: string;
  appCheckDebugToken?: string;
  sentryDsn?: string;
  /** App marketing version, stamped into telemetry (X-Client-Version / Sentry release). */
  appVersion: string;
}

function normalizeDeployEnv(raw: string | undefined): DeployEnv {
  const value = (raw ?? '').trim().toLowerCase();
  return (DEPLOY_ENVS as readonly string[]).includes(value) ? (value as DeployEnv) : 'local';
}

function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Read + validate the typed env subset from a Vite env record. Exposed for testing with an injected
 * record; production calls {@link readAdminEnv} which passes `import.meta.env`.
 */
export function parseAdminEnv(source: ImportMetaEnv): AdminEnv {
  const deployEnv = normalizeDeployEnv(source.VITE_GIVMO_ENV);
  const apiBaseUrl = optional(source.VITE_API_BASE_URL) ?? API_BASE_URL_BY_ENV[deployEnv];

  const firebase: FirebaseWebConfig = {
    apiKey: optional(source.VITE_FIREBASE_API_KEY) ?? '',
    authDomain: optional(source.VITE_FIREBASE_AUTH_DOMAIN) ?? '',
    projectId: optional(source.VITE_FIREBASE_PROJECT_ID) ?? '',
    appId: optional(source.VITE_FIREBASE_APP_ID) ?? '',
    storageBucket: optional(source.VITE_FIREBASE_STORAGE_BUCKET),
    messagingSenderId: optional(source.VITE_FIREBASE_MESSAGING_SENDER_ID),
    measurementId: optional(source.VITE_FIREBASE_MEASUREMENT_ID),
  };

  return {
    deployEnv,
    apiBaseUrl,
    firebase,
    recaptchaEnterpriseSiteKey: optional(source.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY) ?? '',
    appCheckDebugToken: optional(source.VITE_APP_CHECK_DEBUG_TOKEN),
    sentryDsn: optional(source.VITE_SENTRY_DSN),
    appVersion: optional(source.VITE_APP_VERSION) ?? '0.0.0',
  };
}

/** Read the validated env subset from Vite's `import.meta.env`. */
export function readAdminEnv(): AdminEnv {
  return parseAdminEnv(import.meta.env);
}

/** Whether this is a production-grade environment (drives HSTS / debug-log gating). */
export function isProduction(env: AdminEnv): boolean {
  return env.deployEnv === 'prod';
}

/**
 * The five switches the EMPLOYER MODULE reads (EP-MNT3): direct giving, gift cards, occasion programs,
 * steps competitions and join-or-create. Their ONE implementation moved into
 * `@givmo/employer-module` with the pages that read them, and this app's shell and routes read the same
 * functions through this re-export, so a switch can never answer differently on the shell and on the
 * page. They read the env this app HANDS the module at boot (`configureEmployerModuleEnv` in
 * `main.tsx` and `preview/main.tsx`, pinned by `employer-module-env.test.ts`), each still taking an
 * explicit `source` for tests.
 */
export {
  isCompanyDirectGivingEnabled,
  isCompanyGiftCardsEnabled,
  isCompanyJoinOrCreateEnabled,
  isCompanyOccasionProgramsEnabled,
  isCompanyStepsCompetitionsEnabled,
} from '@givmo/employer-module/lib/env';

/**
 * EP-WEB3 dark switch — the COMPANY FUNDRAISERS area (a company creating + managing a fundraiser it
 * OWNS, via `POST /admin/company/fundraisers`). DEFAULT OFF in every environment: while off the whole
 * area is UNREACHABLE (no sidebar entry renders, and the `/company-fundraisers*` routes redirect to
 * `/dashboard`), so an admin never sees a surface whose create/list endpoints 404 at the backend.
 *
 * Activation is a TWO-SIDED flip in one promotion round: set this build-time var
 * `VITE_COMPANY_FUNDRAISERS_ENABLED=true` for the env's admin build AND flip the backend
 * `COMPANY_FUNDRAISER_CREATION_ENABLED` env var for the same env together — a visible area against an
 * OFF backend would 404 at the two `/admin/company/fundraisers` routes. The "Seed this fundraiser" CTA
 * additionally requires `isCompanyDirectGivingEnabled()` (it re-enters the /give flow), so it renders
 * only when BOTH switches are on. Read as a plain `import.meta.env` string; `source` is injectable.
 */
export function isCompanyFundraisersEnabled(source: ImportMetaEnv = import.meta.env): boolean {
  return (source.VITE_COMPANY_FUNDRAISERS_ENABLED ?? '').trim().toLowerCase() === 'true';
}

/**
 * CW-WEB4 dark switch — the Settings → **Public giving card** tab, where a company Owner publishes a
 * selection of the company's own giving aggregates as an embeddable card for the company's website.
 * DEFAULT OFF in every environment: while off the tab is not registered into the visible strip at
 * all (its permission predicate returns false), so the settings surface is byte-identical to today.
 *
 * Activation is a TWO-SIDED flip in one promotion round: set this build-time var
 * `VITE_COMPANY_GIVING_CARD_ENABLED=true` for the env's admin build AND flip the backend
 * `CONNECT_WIDGETS_ENABLED` env var for the same env — both `/admin/company/giving-publication`
 * routes 404 while the backend flag is off (CW-BE6 raises `CompanyGivingPublicationDisabledException`,
 * mapped to the not-found handler), so a visible tab against an OFF backend would fail on load.
 *
 * ★ A third side gates only the EMBED CODE and the preview, not the tab: both need the CW-GO1 hosts
 * ({@link widgetEmbedBaseUrl} / {@link widgetLoaderScriptUrl}). Unconfigured, the tab still publishes
 * — it simply says the embed code is not available in this environment rather than handing an Owner a
 * snippet pointing at a host that does not exist.
 *
 * Read as a plain `import.meta.env` string (Vite injects `VITE_*` at build time); `source` is
 * injectable for tests.
 */
export function isCompanyGivingCardEnabled(source: ImportMetaEnv = import.meta.env): boolean {
  return (source.VITE_COMPANY_GIVING_CARD_ENABLED ?? '').trim().toLowerCase() === 'true';
}

/**
 * CW-WEB4 — the origin serving `apps/embed` (`https://embed.givmo.io` in the shipped topology), used
 * for the bare-iframe snippet and the live preview frame.
 *
 * `null` until CW-GO1 provisions the host for this environment. It is deliberately NOT defaulted to
 * the production origin the way {@link AdminEnv.apiBaseUrl} is: a snippet is COPIED OUT of this
 * screen onto a page Givmo does not control, so a wrong-environment guess would be pasted somewhere
 * permanent, and a preview frame pointed at prod from a staging portal would show an Owner someone
 * else's environment. No value is the honest answer, and the tab says so.
 */
export function widgetEmbedBaseUrl(source: ImportMetaEnv = import.meta.env): string | null {
  return optional(source.VITE_WIDGET_EMBED_BASE_URL) ?? null;
}

/**
 * CW-WEB4 — the full URL of the versioned `widget.js` loader artifact
 * (`https://cdn.givmo.io/widget-v1.js` in the shipped topology), used for the `<script>` + custom
 * element snippet. `null` until CW-GO1 serves it; see {@link widgetEmbedBaseUrl} for why there is no
 * default.
 */
export function widgetLoaderScriptUrl(source: ImportMetaEnv = import.meta.env): string | null {
  return optional(source.VITE_WIDGET_LOADER_URL) ?? null;
}

/**
 * EP-SUITE3 dark switch — the COMPANY CONSOLE's waffle in this portal's top bar. DEFAULT OFF: while
 * off `<AppSwitcher>` renders NO DOM at all, so the employer portal is byte-identical to before.
 *
 * ★ It is the SAME variable `apps/company` reads (`isCompanyConsoleEnabled()` there), and that is the
 * point rather than a convenience: the waffle is an entry point INTO the console, so a portal that
 * revealed it while the console was still dark in the same environment would advertise a destination
 * that renders a "not available here" notice. One variable, both builds, flipped in one round.
 *
 * Both sides are build-time reads, so a flip is `gh variable set` plus a rebuild of EACH app.
 *
 * Read as a plain `import.meta.env` string (Vite injects `VITE_*` at build time); `source` is
 * injectable for tests.
 */
export function isCompanyConsoleEnabled(source: ImportMetaEnv = import.meta.env): boolean {
  return (source.VITE_COMPANY_CONSOLE_ENABLED ?? '').trim().toLowerCase() === 'true';
}

/**
 * EP-AI1 dark switch — the GIVMO ASSISTANT panel (the labelled top-bar control and the right-side
 * companion panel). DEFAULT OFF in every environment: while off NOTHING registers into the shell's
 * assistant slot, so the top bar emits no assistant DOM at all and the panel is unreachable — there
 * is no route to type and no control to find.
 *
 * Activation is a TWO-SIDED flip in one promotion round: set this build-time var
 * `VITE_EMPLOYER_ASSISTANT_ENABLED=true` for the env's admin build AND flip the backend
 * `EMPLOYER_ASSISTANT_ENABLED` env var for the same env — the backend's audience resolution requires
 * BOTH that flag and the consumer master `ASSISTANT_ENABLED`, so a visible panel against an off
 * backend would answer every question on the consumer tool set instead of the company's own data.
 *
 * ★ A THIRD side gates whether the panel can actually answer, and it is not a flag: the organization
 * must have accepted the assistant consent instrument, which no environment serves yet (the copy is
 * signed separately and lands as a seed migration). Until it does, a revealed panel opens, reads its
 * consent state, and says so — which is the honest state, not a bug. Do not read a green build as
 * permission to flip this.
 *
 * Read as a plain `import.meta.env` string (Vite injects `VITE_*` at build time); `source` is
 * injectable for tests.
 */
export function isEmployerAssistantEnabled(source: ImportMetaEnv = import.meta.env): boolean {
  return (source.VITE_EMPLOYER_ASSISTANT_ENABLED ?? '').trim().toLowerCase() === 'true';
}

/**
 * EP-OPS1 interim autoforward (default off) — while on, a visit on the legacy `admin.givmo.io` host
 * redirects to `employer.givmo.io` before the app boots (D-EP-8 as revised 2026-07-09: the portal's
 * canonical URL is employer.givmo.io; company.givmo.io belongs to the org console). App-level on
 * purpose: the hosting-layer 301 (Stage B) stays gated on the WR-6 Flutter rollback anchor standing
 * down, and this forward vanishes with the app on any rollback. Flip only in a round where
 * `employer.givmo.io` is domain-attached, allowlisted (reCAPTCHA + Auth), and CORS-live for the env —
 * forwarding to an origin the backend rejects would strand the customer the forward exists to serve.
 */
export function isAdminDomainForwardEnabled(source: ImportMetaEnv = import.meta.env): boolean {
  return (source.VITE_ADMIN_DOMAIN_FORWARD_ENABLED ?? '').trim().toLowerCase() === 'true';
}

/** The legacy → canonical host pair for the EP-OPS1 forward (prod only; staging/dev branded admin
 * domains still serve the Flutter app and are out of scope until their own cutover). */
const LEGACY_ADMIN_HOST = 'admin.givmo.io';
const CANONICAL_ADMIN_ORIGIN = 'https://employer.givmo.io';

/**
 * Compute the forward URL for a location on the legacy admin host, or `null` when no forward
 * applies (flag off, or already on the canonical/raw/local host). Pure + injectable for tests;
 * `main.tsx` applies the result with `location.replace` (no history entry) before any boot work.
 */
export function computeLegacyAdminForwardUrl(
  location: Pick<Location, 'hostname' | 'pathname' | 'search' | 'hash'>,
  source: ImportMetaEnv = import.meta.env,
): string | null {
  if (!isAdminDomainForwardEnabled(source)) return null;
  if (location.hostname !== LEGACY_ADMIN_HOST) return null;
  return `${CANONICAL_ADMIN_ORIGIN}${location.pathname}${location.search}${location.hash}`;
}
